// 此文用于定于前后端数据转换过程中需要的一些类型
import type {
    ActivityProtocol,
    AnyObject,
    ApplicationAbstract,
    ApplicationSettingTheme,
    ApplicationSettingThemeNavBarMode,
    ApplicationType,
    ApplicationVersionBranch,
    ApplicationWebsiteSetting,
    BlockAbstract,
    BlockType,
    BreakPoint,
    ChartLikeProtocol,
    CustomStatisticRequestField,
    DataSourceAbstract,
    DataSourceIdentity,
    EndResolvedArg,
    Field,
    FieldCellValue,
    FieldInputValue,
    FieldValue,
    FilterCommonCondition,
    FilterFormType,
    FormRule,
    JoinSetting,
    NavigationShowMode,
    PageOpenType,
    PaginationProtocol,
    QuotaProtocols,
    RecordLikeProtocol,
    SelectedMode,
    Sorter,
    SpaceVersion,
    TypeInstanceMap,
    UserProtocol,
    ViewOptions
} from '@lighthouse/core'
import type {
    AliyunSmsAccountItem,
    DeepSeekAccountItem,
    DingTalkRobotItem,
    EmailAccountItem,
    EnvDiffs,
    FlowEdge,
    FlowNode,
    FlowRunStatus,
    StripeAccountItem,
    UsedWorkflow,
    WeChatPayAccountItem,
    WeChatPublicAccountItem,
    Workflow,
    WorkflowType,
    WxRobotItem
} from '@lighthouse/shared'
import type { MakeADT } from 'ts-adt/MakeADT'

import type { TemplateComponent } from '@/atoms/application/type'

/**
 * 开始   接口类型定义（新）
 *
 */

// --------------------------------------------------------------------
// payload
// --------------------------------------------------------------------
export type GetRecordPayload = {
    appId: string
    envId: string
    dsId: string
    recordId: string
}

export type CreateRecordPayload = DataSourceIdentity & {
    id: string
    content?: RecordLikeProtocol['content']
    beforeId?: string
}

export type GetRecordByFilterPayload = {
    appId: string
    envId: string
    dsId: string
    filter?: FilterFormType
    sorts?: Sorter[]
}

export type CreateBreakPointPayload = BreakPoint

export type UpdateBreakPointPayload = {
    id: string
} & Partial<Omit<BreakPoint, 'id'>>

export type UpdateRecordPayload = DataSourceIdentity & {
    recordId: string
    fields: { fieldId: string; value: FieldInputValue | undefined }[]
}

export type GetNodePreviewDataPayload = DataSourceIdentity & { nodeId: string }

export type SubmitFormPayload = {
    appId: string
    envId: string
    dsId: string
    pageId: string
    blockId: string
    isSyncComponent?: boolean
    code?: string
    recordId?: string
    validator?: {
        rules?: FormRule[]
        codeValidator?: Record<string, string>
    }
    fields: {
        fieldId: string
        value: FieldInputValue
        isCheckRepeat?: boolean
    }[]
    subFormRecord?: { subFormId: string; subFormContent: { recordId: string; columns: { columnId: string; value: FieldInputValue }[] }[] }[]
}

export type getListSyncEnvDsPayload = {
    envId: string
    dsId: string
}

export type SwitchSyncDsPayload = {
    envId: string
    dsId: string
    switchEnvId: string
}

export type GetBeUsedSubProcessPayload = {
    workflowId: string
}

// --------------------------------------------------------------------
// res
// --------------------------------------------------------------------
export type DataSourceRes = Omit<DataSourceAbstract, 'appId' | 'envId'>

export type RecordRes = Omit<RecordLikeProtocol, 'appId' | 'envId' | 'dsId'>

export type DataSourceAndRecordsRes = {
    datasource: DataSourceRes
    records: RecordRes[]
}

/** 获取指定行数据 */
export type PageRecordRes = {
    datasource: DataSourceRes
    record: RecordRes
}

export type ViewDataRes = {
    datasource: DataSourceRes
    records: RecordRes[]
    config: {
        view: ViewOptions
    }
}

export type GetListSyncEnvDsRes = {
    envName: string
    envId: string
    exist: boolean
}[]

export type TemplateComponentsRes = TemplateComponent<
    Omit<BlockAbstract, 'config'> & {
        config: Record<BlockType, AnyObject>
    }
>[]

export type BreakPointRes = BreakPoint[]

export type GetAppOfOutsideRes = Omit<ApplicationAbstract, 'version'> & {
    version: ApplicationVersionBranch & {
        config: Pick<ApplicationWebsiteSetting, 'authentication' | 'navbar' | 'language' | 'theme'>
        breakPointList: BreakPoint[]
    }
}

export type ActiveSubProcessRes = {
    success: boolean
    outputArgs: EndResolvedArg[]
}

// --------------------------------------------------------------------
// result
// --------------------------------------------------------------------
/** 获取指定行数据 */
export type PageRecordResult = {
    datasource: DataSourceAbstract
    record: RecordLikeProtocol
}

export type DataSourceAndRecordsResult = {
    datasource: DataSourceAbstract
    records: RecordLikeProtocol[]
}

export type ViewDataResult = {
    datasource: DataSourceAbstract
    records: RecordLikeProtocol[]
    config: {
        view: ViewOptions
    }
}

export type AppsByEnvResult = {
    id: string // 版本id
    serial: string // 版本序号
    name: string // 版本名称
}[]

export type LatestActivityResult = {
    content: string
    icon: string
    notificationId: string
    url: string
}

export type ActivityListResult = ActivityProtocol[]

export type PromoInviteResult = {
    users: UserProtocol[]
    invitationCount: number
}

export type SpacePlansResult = {
    originalPrice: number
    realPrice: number
    spacePlan: SpaceVersion
}[]

export type UsedWorkflowResult = UsedWorkflow[]

export type GetAppOfOutsideResult = ApplicationAbstract & { version: { breakPointList: BreakPoint[] } }
/**
 * 结束   接口类型定义（新）
 *
 */
export interface TokenResult {
    headerName: string
    parameterName: string
    token: string
}

export interface WechatResult {
    mark: string
    url: string
}

export interface EnvironmentIdentity {
    envId: string
}

export type GetDsMetaPayload = Omit<DataSourceIdentity, 'appId'>

export interface CreateApplicationPayload {
    spaceId: string
    name: string
    icon: string
    type: ApplicationType
    domainPrefix: string
    theme: ApplicationSettingTheme
    themeMode: ApplicationSettingThemeNavBarMode
    navigationShowMode: NavigationShowMode
}

export type CreateDataSourcePayload = {
    envId: string
    type: number
}

export type DuplicateAndInitDsPayload = Omit<DataSourceIdentity, 'appId'>

export type CreateSyncDsPayload = {
    envId: string
    syncAppId: string
    syncEnvId: string
    syncDsId: string
}

export type CreateJoinDsPayload = {
    envId: string
    primaryDsId: string
    config: JoinSetting
}

export type CreateDataSourceResult = {
    name: string
    dsId: string
}

export type DeleteDataSourcePayload = Omit<DataSourceIdentity, 'appId'>

export type GetDataSourcePayload = {
    appId: string
    envId: string
    dsId: string
    pagination: PaginationProtocol['pagination']
    search?: string
    filter?: FilterFormType
    linkFilter?: FilterFormType
    sorts?: Sorter[]
    currentRecordId?: string
    parentRecordId?: string
    disabledFilter?: FilterFormType
    recordIds?: string[]
    abort?: AbortController
}

export type getDsCountPayload = {
    envId: string
    dsId: string
    search?: string
    filter?: FilterFormType
    linkFilter?: FilterFormType
    currentRecordId?: string
    parentRecordId?: string
    recordIds?: string[]
    abort?: AbortController
}

export type MoveDsPayload = {
    envId: string
    id: string
    before: string
    after: string
}

export type GroupRecordCountPayload = {
    appId: string
    viewId: string
    isSyncComponent?: boolean
    fieldId: string
    quickFilters?: FilterCommonCondition[]
    linkFilter?: FilterFormType
    filter?: FilterFormType
    search?: string
    abort?: AbortController
}

export type ChartData = {
    datasource: DataSourceAbstract
    chart: ChartLikeProtocol
}

export type ViewSettingParams = {
    filter: FilterFormType | undefined
    quickFilters?: FilterCommonCondition[]
    sorts?: Sorter[]
    linkFilter?: FilterFormType
}

export type GetViewCustomStatisticPayload = {
    appId: string
    envId: string
    pageId: string
    viewId: string
    uniqueIdField?: string
    uniqueIdValues: unknown[]
    viewRows?: { uniqueIdValue: unknown; values: Record<string, unknown> }[]
    fields: CustomStatisticRequestField[]
    abort?: AbortController
}

export type UpdateCellPayload = Omit<DataSourceIdentity, 'appId'> & {
    id: string
    fieldId: string
    content: FieldValue
}

export type CreateFieldResult = {
    innerType: TypeInstanceMap
    fieldId: string
}

export type UpdateFieldPayload = Omit<DataSourceIdentity, 'appId'> & {
    id: string
    name?: string
    config?: AnyObject
    type?: Field['type']
}

export type DeleteFieldPayload = Omit<DataSourceIdentity, 'appId'> & {
    id: string
}

export type CreateFieldPayload = Omit<DataSourceIdentity, 'appId'> & {
    type: string
    name?: string
    config?: AnyObject
    beforeId?: string
}

export type UpdateDataSourcePayload = Partial<DataSourceAbstract> &
    EnvironmentIdentity & {
        id: string
    }

export type DeleteRecordPayload = Omit<DataSourceIdentity, 'appId'> & {
    search?: string
    mode?: SelectedMode
    recordIds?: string[]
    filter?: FilterFormType
    linkFilter?: FilterFormType
}

export type GetDsGraphViewPayload = {
    blockId: string
    isSyncComponent?: boolean
    currentRecordId?: string
    parentRecordId?: string
    linkFilter?: FilterFormType
    FilterFormType?: FilterFormType
    abort?: AbortController
}

export interface UpdatePagePayload {
    id: string
    name?: string
    icon?: string
    open?: PageOpenType
    sorts?: Sorter[]
    filter?: FilterFormType
    isHome?: boolean
    roleIds?: string[]
}

export type CreateBlockPayload = { pageId: string; addedBlocks: BlockAbstract[]; allBlocks: BlockAbstract[]; skipInspection?: boolean }
export type UpdateBlockPayload = { pageId: string; updatedBlocks?: BlockAbstract[]; allBlocks?: BlockAbstract[] }

/**
 * 工作流
 * ------------------------------ start
 * */

export type WorkflowCreatePayload = Pick<Workflow, 'type' | 'name' | 'description' | 'runType'> & {
    nodes?: FlowNode[]
    edges?: FlowEdge[]
}

export type WorkflowResult = Workflow[]

export enum RunDev {
    'PROD' = 'PROD',
    'TEST' = 'TEST'
}

export type WorkflowRunLog = {
    name: string
    versionId: string
    workflowType: WorkflowType
    status: FlowRunStatus
    runEnv: RunDev
    envId: string
    envName: string
    operator: string
    executionTime: number
    workflowInstanceId: string
}

export type GetWorkflowRunLogsPayload = Partial<Pick<WorkflowRunLog, 'status' | 'runEnv' | 'envId'>> & {
    workflowId?: string
    pageSize?: number
    currentPage?: number
}

export type WorkflowRunLogRes = {
    list: WorkflowRunLog[]
    currentPage: number
    pageSize: number
    total: number
}

export type UpdateWorkflowPayload = Pick<Workflow, 'name' | 'description' | 'runType'> & {
    id: string
    nodes: FlowNode[]
    edges: FlowEdge[]
}

export type UpdateWorkflowResult = {
    errorMap: Record<string, boolean>
    success: boolean
}

/**
 * 工作流
 * ------------------------------ end
 */

export type SpaceQuota = {
    spaceName: string
    currentVersion: SpaceVersion
    currentVersionCode?: number
    // -1 为无限制 默认为到期时间戳
    expiredAt: number
    // numberOfApps: QuotaProtocols
    // numberOfWorkflowExecutions: QuotaProtocols
    // numberOfAiInvocation: QuotaProtocols
    fileStorage: QuotaProtocols
    numberOfRecords: QuotaProtocols
    numberOfUsers: QuotaProtocols
    numberOfDomain: QuotaProtocols
    numberOfUv: QuotaProtocols
    // 更新次数
    numberOfUpdates: QuotaProtocols
    // 数据表查询算力
    // numberOfCu: QuotaProtocols
    // 可发布应用数量
    numberOfPublishedApps: QuotaProtocols
    // 用户阿里云视频用量

    traffic: {
        // 新增
        packs: (QuotaProtocols & {
            startTime: number // 13位时间戳 开始时间
            expiredAt?: number // 13位时间戳 过期时间， 如果没有过期时间则表示永久有效，
            // 流量类型，INCLUDE代表版本内置流量，PURCHASED 代表增购流量
            packType: 'INCLUDE' | 'PURCHASED'
        })[]
    } & QuotaProtocols
    // api执行次数
    apiInvoke: QuotaProtocols
    sms: QuotaProtocols
    llmToken: QuotaProtocols
    embeddingToken: QuotaProtocols
}

export type SpaceVersionInfo = {
    /**
     * 空间中可发布的应用数量
     */
    publishedAppNumLimit?: number
    /**
     * 绑定自有域名的应用数量
     */
    domainNumLimit?: number
    /**
     * 费用
     */
    price?: number
    /**
     * 空间中所有应用的数据表中记录的数据统计，包括空白记录
     */
    recordsLimit?: number
    /**
     * 是否包含团队训练配额
     */
    teamTraining?: boolean
    /**
     * uv配额
     */
    uvLimit?: number
    /**
     * 空间内所有应用内上传附件的大小统计(单位:MB)
     */
    storageLimit?: number
    /**
     * 空间中所有应用的用户数量
     */
    userNumLimit?: number
    /**
     * 当前付费的版本, 有以下枚举值 ULTIMATE：旗舰版 PROFESSIONAL:专业版 ENTERPRISE : 企业版  STANDARD：创业版 FREE：免费版
     */
    version: SpaceVersion
    /**
     * 是否显示byecode广告位
     * */
    byecodeBranding?: boolean
    /**
     * 是否包含客户经理服务
     */
    customerManagerServices: boolean
    /**
     * 更新记录次数
     */
    updatesLimit?: number
    /**
     * 数据表查询算力
     */
    cuLimit?: number
    /**
     * 视频流量
     */
    traffic?: number
}

export type NetworkTraffic = {
    totalTraffic: QuotaProtocols & { expiredTime: number }
    freeTraffic: QuotaProtocols & { expiredTime: number }
    versionTraffic: QuotaProtocols & { expiredTime: number }
    purchaseTraffic: (QuotaProtocols & { expiredTime: number })[]
}

export type WechatShareResult = {
    title: string
    subtitle: string
    icon: string
}

export type UpdateIntegrationPayload = MakeADT<
    'type',
    {
        WE_COM_MAIL: { config: Partial<EmailAccountItem>; type: 'WE_COM_MAIL'; id: string }
        ALIYUN_SMS: { config: Partial<AliyunSmsAccountItem>; type: 'ALIYUN_SMS'; id: string }
        NET_EASE_126_MAIL: { config: Partial<EmailAccountItem>; type: 'NET_EASE_126_MAIL'; id: string }
        WE_COM_ROBOT: { config: Partial<WxRobotItem>; type: 'WE_COM_ROBOT'; id: string }
        DING_TALK_ROBOT: { config: Partial<DingTalkRobotItem>; type: 'DING_TALK_ROBOT'; id: string }
        WECHAT_OFFICIAL_ACCOUNT: { config: Partial<WeChatPublicAccountItem>; type: 'WECHAT_OFFICIAL_ACCOUNT'; id: string }
        WECHAT_PAY: { config: Partial<WeChatPayAccountItem>; type: 'WECHAT_PAY'; id: string }
        STRIPE: { config: Partial<StripeAccountItem>; type: 'STRIPE'; id: string }
        DEEP_SEEK: { config: Partial<DeepSeekAccountItem>; type: 'DEEP_SEEK'; id: string }
        SEARCH_API: { config: Partial<AnyObject>; type: 'SEARCH_API'; id: string }
        QWEN: { config: Partial<AnyObject>; type: 'QWEN'; id: string }
        ERNIE_BOT: { config: Partial<AnyObject>; type: 'ERNIE_BOT'; id: string }
        ZHI_PU_AI: { config: Partial<AnyObject>; type: 'ZHI_PU_AI'; id: string }
        MOONSHOT_AI: { config?: Partial<AnyObject>; type: 'MOONSHOT_AI'; id: string }
    }
>

export type updatePasswordPayload = {
    mobile: string
    smsCode: string
    password: string
}

export enum PAY_TYPE {
    ALI_PAY = 'ALI_PAY',
    WE_CHAT_PAY = 'WE_CHAT_PAY'
}

export type SpaceOrder = {
    outTradeNo: string // 订单号,
    paidContent: string // 付费项目,
    amount: number // 金额，单位元,
    payMethod: PAY_TYPE // 支付方式,
    successTime: string // 订单支付时间
    spaceName: string // 空间名称
    spaceId: string // 空间id
}

export type EnvDiffsResult = EnvDiffs

export enum WxProgramsAuditStatus {
    // 未提交审核
    NOT_SUBMITTED = 'NOT_SUBMITTED',
    // 审核中
    AUDITING = 'AUDITING',
    // 审核通过
    PASSED = 'PASSED',
    // 审核失败
    FAILED = 'FAILED'
}

export interface WxMiniProgramsAuditInfo {
    hasUploadAvatar: boolean // 上传小程序头像
    hasFilledName: boolean // 填写小程序名称
    hasSelectedCategory: boolean // 选择服务类目
    isFunctionalComplete: boolean // 功能完整且非测试版
    hasReadSpecification: boolean // 已阅读运营规范
    description: string // 版本更新说明
    screenshots: string[] // 截图URL（至少3张）
    screenRecords?: string[] // 录屏URL（可选）
    testNotes?: string // 测试备注
    version: string // 版本号
}

export interface WxMiniProgramsError {
    appName: string
    spaceName: string
    msg: string
}

export interface MiniProgramEntity {
    // 核心标识
    appId: string // 小程序唯一标识
    // 基础信息
    miniProgramInfo: {
        name: string // 小程序名称
        avatar: string // 小程序头像
    }
    auditInfo: {
        status: WxProgramsAuditStatus // 审核状态
        auditCreateTime?: number // 审核提交时间版本号
    } & WxMiniProgramsAuditInfo
    releaseInfo: {
        published: boolean // 是否已发布正式版
        // releaseCreateTime?: number // 正式版提交时间
        releaseUpdateTime?: number // 正式版更新时间
    } & WxMiniProgramsAuditInfo
}

// 微信小程序授权状态
export enum WxProgramsAuthorizationStatus {
    AUTH_SUCCESS = 'AUTH_SUCCESS',

    AUTH_FAIL = 'AUTH_FAIL',

    AUTHORIZING = 'AUTHORIZING'
}
